import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldStar, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const AUTH_BG =
  "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxwb2xpY2UlMjBvZmZpY2VyJTIwc2lsaG91ZXR0ZSUyMGRhcmt8ZW58MHx8fHwxNzgyMDU4NTMyfDA&ixlib=rb-4.1.0&q=85";

export function PasswordField({ id, label, value, onChange, testId, invalid, labelRight }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        {labelRight}
      </div>
      <div className="relative mt-1.5">
        <Input
          id={id}
          type={show ? "text" : "password"}
          required
          data-testid={testId}
          aria-invalid={invalid || undefined}
          className={`bg-secondary/50 pr-11 ${invalid ? "border-destructive focus-visible:ring-destructive" : ""}`}
          value={value}
          onChange={onChange}
          placeholder="••••••••"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          data-testid={`${testId}-toggle`}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
        >
          {show ? <EyeSlash className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}

export default function Login({ defaultMode = "login" }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(defaultMode);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const mismatch = mode === "register" && form.confirm.length > 0 && form.password !== form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && form.password !== form.confirm) {
      toast.error("Passwords don't match. Please re-enter them.");
      return;
    }
    setLoading(true);
    const res =
      mode === "login"
        ? await login(form.email, form.password)
        : await register(form.email, form.password, form.name);
    setLoading(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Welcome back." : "Account created.");
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src={AUTH_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0B1121]/80" />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-primary flex items-center justify-center">
            <ShieldStar className="h-7 w-7 text-primary-foreground" weight="fill" />
          </div>
          <span className="text-2xl font-black tracking-tight">MyPayAlert</span>
        </div>
        <div className="relative">
          <p className="overline text-primary mb-3">Payroll &amp; Earnings</p>
          <h2 className="text-4xl font-black tracking-tight leading-tight max-w-md">
            Take control of every paycheck.
          </h2>
          <p className="text-muted-foreground mt-4 max-w-sm">
            Track regular pay, overtime, shift premiums, paid duty and bonuses — with accurate future paycheck estimates.
          </p>
        </div>
        <div className="relative text-xs text-muted-foreground">Built for every worker who earns a paycheck</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <ShieldStar className="h-7 w-7 text-primary" weight="fill" />
            <span className="text-xl font-black tracking-tight">MyPayAlert</span>
          </div>
          <h1 className="text-3xl font-black tracking-tight">
            {mode === "login" ? "Sign in" : "Create account"}
          </h1>
          <p className="text-muted-foreground mt-2 mb-8 text-sm">
            {mode === "login" ? "Access your payroll dashboard." : "Start tracking your pay for free."}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  className="mt-1.5 bg-secondary/50"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Officer J. Doe"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="email-input"
                className="mt-1.5 bg-secondary/50"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@department.ca"
              />
            </div>
            <PasswordField
              id="password"
              label="Password"
              testId="password-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              labelRight={mode === "login" && (
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-primary hover:underline">
                  Forgot password?
                </Link>
              )}
            />
            {mode === "register" && (
              <div>
                <PasswordField
                  id="confirm-password"
                  label="Confirm password"
                  testId="confirm-password-input"
                  invalid={mismatch}
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                />
                {mismatch && (
                  <p className="text-xs text-destructive mt-1.5" data-testid="password-mismatch-error" role="alert">
                    Passwords don't match.
                  </p>
                )}
              </div>
            )}
            {mode === "register" && (
              <p className="text-xs text-muted-foreground">You'll set your country, organization and role when you open the Shift Marketplace.</p>
            )}
            <Button
              type="submit"
              disabled={loading || mismatch}
              data-testid="auth-submit-button"
              className="w-full rounded-md font-semibold"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "login" ? "New to MyPayAlert?" : "Already have an account?"}{" "}
            <button
              type="button"
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-primary font-semibold hover:underline"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
